import { useSelector } from 'react-redux'
import config from '../config.json'
import { filterCanViewFullApp } from './accountPermissionsUtils'
import { filterCanEditPersonalData } from './familyPermissionsUtils'

export const isSelectFamilyMember = state => {
  return !!state.account?.selectedMember
}

export const selectMember = state => {
  const { selectedMember, members } = state.account

  const filteredMembers = members?.filter(({ id }) => id === selectedMember)
  if (filteredMembers?.length) return filteredMembers[0]
  return null
}

export const canRemoveMembers = state => {
  const { permissions } = state.account
  return permissions.filter(perm => perm === 'remove_member')
}

export const canGrantPermissions = state => {
  const { permissions } = state.account
  return permissions.filter(perm => perm === 'grant_permissions')
}

export const isFamilyPlanActive = state => true
// state.payment.my_subscriptions?.some(
//   plan => plan.plan_details.type === 'family'
// )

export const isMembershipPlanActive = my_subscriptions =>
  my_subscriptions?.some(plan => plan.plan_details?.is_membership)

export const ShouldShowMembershipPlanActive = fn => {
  const blocked = typeof item === 'function' ? () => null : null

  return useSelector(state => isMembershipPlanActive(state)) ? fn : blocked
}

const participatePermissionsFilter = permission =>
  ['participate_in_doctor_visit', 'participate_in_doctor_visit_minor'].includes(
    permission.desc
  ) && permission.status === 'approved'

export const memberIstMe = (member, state) =>
  member.id === state.profile.userDetails.id

export const canJoinAppointment = member =>
  member.permissions.requested_permissions.filter(participatePermissionsFilter)
    .length > 0

export const isChildOfAdmin = state => {
  const parentAdmins = state.account.members
    ? Object.values(state.account.members).filter(
        member =>
          member.relation === 'parent' &&
          parseInt(member.id) === state.profile.owner
      )
    : []
  return parentAdmins.length > 0
}

const isNotChildOfAdmin = state => {
  const isChildOf = isChildOfAdmin(state)
  return !isChildOf
}

export const isAdult = state => {
  var age = state.profile?.userDetails?.age
  var isFamily = isFamilyPlanActive(state)
  if (!isFamily || age >= config.minimalAdultAge) return true
  return isNotChildOfAdmin(state)
}

export const isMinor = state => {
  var age = state.profile?.userDetails?.age
  return age < config.minimalAdultAge
}

export const myChildren = members => {
  return members?.filter(member => member.relation === 'child')
}

export const myMinorChildren = members => {
  if (!members?.length || !myChildren(members)?.length) return []
  return myChildren(members).filter(
    member => getAge(member.dateOfBirth) <= config.minimalAdultAge
  )
}

export const getValidFamilyMemberIds = (state, minorChildrenOnly = false) => {
  const defaultUser = state.profile?.userDetails?.id
  const { activeMembers } = state.account
  if (!activeMembers?.length) return [defaultUser]

  const familyMemberIds = [defaultUser].concat(
    minorChildrenOnly
      ? myMinorChildren(activeMembers).map(child => child.id)
      : activeMembers.map(member => member.id)
  )

  return familyMemberIds.filter(id => id != null)
}

export const hasChildren = members => {
  return myChildren(members) && myChildren(members).length > 0
}

export const isMinorChild = (member, members) => {
  const minorChildrenIds = myMinorChildren(members).map(member =>
    parseInt(member?.id)
  )
  return minorChildrenIds.includes(parseInt(member))
}

export const getAge = birthDate =>
  Math.floor((new Date() - new Date(birthDate).getTime()) / 3.15576e10)

export const isLegalyChild = state => {
  const isChild = state.account.newMember.relation === 'child'
  const age = getAge(state.account.newMember.dateOfBirth)

  return isChild && age <= config.minimalAdultAge
}

export const isMemberMinorChild = member => {
  return (
    getAge(member?.dateOfBirth) <= config.minimalAdultAge &&
    member?.relation === 'child'
  )
}

export const isMemberMinor = member => {
  return getAge(member?.dateOfBirth) <= config.minimalAdultAge
}

export const RemoveForKids = fn => {
  const blocked = typeof item === 'function' ? () => null : null
  return useSelector(state => filterCanViewFullApp(state)) ? fn : blocked
}

export const RemoveForAdults = fn => {
  return useSelector(state => !isAdult(state)) ? fn : () => null
}

export const getRequestingMember = member => {
  const { permissions } = member
  let requestingMember = false
  permissions?.granted_permissions?.forEach(permmision => {
    if (permmision?.status === 'requested') {
      requestingMember = member
    }
  })
  return requestingMember
}

export const getFirstRequestingPermmisionsMember = members => {
  let firstMember = false
  members?.forEach(member => {
    if (getRequestingMember(member)) {
      firstMember = member
    }
  })
  return firstMember
}

export const getMemberColor = memberIndex => {
  // we have several array of colors for member,
  // we need to check if the member list is bigger than the palette, if so, we need to go over the list again and take the next item from start
  // for example, palette of 5 colors with 6 member, we will get the first color twice (member number 6 will get the 1 color)
  const MEMBER_COLORS = ['#6E7BF4', '#8FBF29', '#D3E3B0', '#FFCE48']
  const colorArraySize = MEMBER_COLORS?.length
  return memberIndex > colorArraySize - 1
    ? getMemberColor(memberIndex - colorArraySize)
    : MEMBER_COLORS?.[memberIndex]
}

export const getMemberNameById = (members, memberId) => {
  if (!members) return ''
  return members?.find(member => member.hmo_member_id === memberId)?.firstName
}

export const getFamilyEmergencyContacts = (activeMembers, userDetails) => {
  //from each member in the family, we need to get the emergency contact and the member id
  //we will return an array of objects with the member id and the emergency contact
  const allEmergencyContacts = []
  allEmergencyContacts.push({
    memberId: userDetails?.id,
    contactName: userDetails?.emergencyContact?.contactName,
    phoneNumber: userDetails?.emergencyContact?.phoneNumber,
    canEdit: true
  })

  activeMembers?.forEach(member => {
    const emergencyContact = member?.emergencyContact
    if (emergencyContact) {
      allEmergencyContacts.push({
        memberId: member?.id,
        contactName: emergencyContact?.contactName,
        phoneNumber: emergencyContact?.phoneNumber,
        canEdit: filterCanEditPersonalData(member)
      })
    }
  })
  return allEmergencyContacts
}

export const getFamilyPharmacies = (activeMembers, userDetails) => {
  //from each member in the family, we need to get the pharmacy details and the member id
  //we will return an array of objects with the member id and the pharmacy details
  const allPharmacies = []
  allPharmacies.push({
    memberId: userDetails?.id,
    pharmacy: userDetails?.pharmacy,
    canEdit: true
  })

  activeMembers?.forEach(member => {
    allPharmacies.push({
      memberId: member?.id,
      pharmacy: member?.pharmacy,
      canEdit: filterCanEditPersonalData(member)
    })
  })
  return allPharmacies
}
